import { useEffect, useRef, useState } from "react";
import { X, Download, Share2, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

// Renders a branded, downloadable Scripture card (navy / warm gold / cream) to a PNG.
export function ScriptureCard({ verse, onClose }) {
  const canvasRef = useRef(null);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!verse) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    c.width = W; c.height = H;

    // Background
    ctx.fillStyle = "#0F172A"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#12203b"; ctx.fillRect(60, 60, W - 120, H - 120);
    // Gold frame
    ctx.strokeStyle = "#C9A44C"; ctx.lineWidth = 3;
    ctx.strokeRect(80, 80, W - 160, H - 160);

    // Top label
    ctx.fillStyle = "#C9A44C";
    ctx.font = "600 26px Georgia";
    ctx.textAlign = "center";
    ctx.fillText("I  N E E D  A  P R O V E R B", W / 2, 175);

    // Scripture text (wrapped)
    const text = `“${verse.text}”`;
    ctx.fillStyle = "#FBF7EE";
    ctx.font = "italic 46px Georgia";
    const maxWidth = W - 260;
    const words = text.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    const lineH = 62;
    const blockH = lines.length * lineH;
    let y = H / 2 - blockH / 2;
    for (const ln of lines) { ctx.fillText(ln, W / 2, y); y += lineH; }

    // Divider + reference
    ctx.strokeStyle = "#C9A44C"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2 - 60, y + 24); ctx.lineTo(W / 2 + 60, y + 24); ctx.stroke();
    ctx.fillStyle = "#C9A44C";
    ctx.font = "600 34px Georgia";
    ctx.fillText(verse.reference, W / 2, y + 84);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "400 22px Arial";
    ctx.fillText(verse.translation || "World English Bible", W / 2, y + 122);

    // Footer
    ctx.fillStyle = "#FBF7EE";
    ctx.font = "600 26px Georgia";
    ctx.fillText("INeedAProverb.com", W / 2, H - 160);
    ctx.fillStyle = "#C9A44C";
    ctx.font = "600 20px Arial";
    ctx.fillText("F R E E   T O   U S E", W / 2, H - 120);

    c.toBlob((blob) => { if (blob) setUrl(URL.createObjectURL(blob)); }, "image/png");
  }, [verse]);

  if (!verse) return null;

  const filename = `proverb-${(verse.reference || "card").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;

  const download = () => {
    if (!url) return;
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    track("card_download", { path: window.location.pathname });
    toast.success("Card downloaded.");
  };
  const shareCard = async () => {
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "I Need a Proverb", text: `${verse.reference} — INeedAProverb.com` });
        track("card_share", { path: window.location.pathname, channel: "native" });
      } else {
        await navigator.share({ title: "I Need a Proverb", text: `“${verse.text}” — ${verse.reference}\nINeedAProverb.com` });
      }
    } catch (e) { if (e?.name !== "AbortError") download(); }
  };
  const body = encodeURIComponent(`“${verse.text}” — ${verse.reference}\nINeedAProverb.com`);
  const viaEmail = () => { track("card_share", { path: window.location.pathname, channel: "email" }); window.open(`mailto:?subject=${encodeURIComponent("A Proverb for you")}&body=${body}`, "_blank"); };
  const viaText = () => { track("card_share", { path: window.location.pathname, channel: "sms" }); window.location.href = `sms:?&body=${body}`; };

  return (
    <div data-testid="scripture-card-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-8 overflow-auto">
      <div className="relative w-full max-w-md rounded-3xl bg-[#FDFBF7] p-5 sm:p-6">
        <button data-testid="card-close-btn" onClick={onClose} className="absolute right-4 top-4 text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold text-center mb-3">Shareable Scripture card</p>
        <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white">
          {url ? <img data-testid="card-preview" src={url} alt={verse.reference} className="w-full h-auto block" />
               : <canvas ref={canvasRef} className="w-full h-auto block" />}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="card-download-btn" onClick={download} className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0F172A] text-white text-sm font-semibold"><Download className="w-4 h-4" /> Download</button>
          <button data-testid="card-share-btn" onClick={shareCard} className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#2C4C3B] text-white text-sm font-semibold"><Share2 className="w-4 h-4" /> Share</button>
          <button data-testid="card-text-btn" onClick={viaText} className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#E2E8F0] text-[#475569] text-sm font-medium"><MessageSquare className="w-4 h-4" /> Text</button>
          <button data-testid="card-email-btn" onClick={viaEmail} className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#E2E8F0] text-[#475569] text-sm font-medium"><Mail className="w-4 h-4" /> Email</button>
        </div>
        <p className="text-center text-xs text-[#94A3B8] mt-3">Free to use · INeedAProverb.com</p>
      </div>
    </div>
  );
}
